import { useDefaultValues } from "./defaultValues";
import { PropValidator, ValidationMessage } from "./entityStore";
import { formatDate, formatNumber, getResource, numberFormatByLandCode } from "./localization";
import { KeysOfTypeGet, getDecimalPlaces } from "./utils";
import { validate as validateEmail } from "email-validator";

type ValidationMessages = ValidationMessage[] | ValidationMessage | undefined | null | false;

function getText(key: string, defaultMessage: string) {
    return getResource("Ui.Validation." + key, defaultMessage) as string;
}

export function getInvalidValueText() {
    return getText("InvalidValue", "Invalid value.");
}

export function getInvalidCharacterText() {
    return getText("InvalidCharacter", "Invalid character '{0}' ({1}).");
}

export function getRecommendedFieldText() {
    return getText("Recommended", "Recommended field.");
}

export function propValueValidator<T, U extends keyof T>(prop: U, getMessages: (value: T[U], entity: T) => ValidationMessages): PropValidator<T> {
    return (entity: T) => {
        const messages = getMessages(entity[prop], entity);
        return {
            prop: prop,
            messages: !messages ? [] : !Array.isArray(messages) ? [messages] : messages,
        };
    };
}

export function propLengthMin<T>(prop: KeysOfTypeGet<T, string | null | undefined>, minLength: number, customMessage?: ValidationMessage): PropValidator<T> {
    return propEmptyOr(prop, propValueValidator(prop, p => ((p || "") as string).length < minLength &&
        (customMessage ?? `Minimum length is ${formatNumber(minLength, useDefaultValues.getState().clientData.landCode, false)}.`)));
}

export function propLengthMax<T>(prop: KeysOfTypeGet<T, string | null | undefined>, maxLength: number, customMessage?: ValidationMessage): PropValidator<T> {
    return propEmptyOr(prop, propValueValidator(prop, p => ((p || "") as string).length > maxLength &&
        (customMessage ?? `Maximum length is ${formatNumber(maxLength, useDefaultValues.getState().clientData.landCode, false)}.`)));
}

export function propLength<T>(prop: KeysOfTypeGet<T, string | null | undefined>, minLength: number, maxLength: number, customMessage?: ValidationMessage): PropValidator<T> {
    return combine([propLengthMin(prop, minLength, customMessage), propLengthMax(prop, maxLength, customMessage)]);
}

export function propInvalidCharacter<T, U extends KeysOfTypeGet<T, string | undefined>>(prop: U, character: string): PropValidator<T> {
    return propEmptyOr(prop, propValueValidator(prop, p => ((p || "") as string).indexOf(character) >= 0 && `Field contains invalid character '${character}'.`));
}

export function propRegex<T>(prop: KeysOfTypeGet<T, string | null | undefined>, regex: RegExp, messages: ValidationMessages): PropValidator<T> {
    return propEmptyOr(prop, propValueValidator(prop, p => regex.test((p || "") as string) && messages));
}

export function propNotRegex<T, U extends KeysOfTypeGet<T, string | null | undefined>>(prop: U, regex: RegExp, messages: ValidationMessages): PropValidator<T> {
    return propEmptyOr(prop, propValueValidator(prop, p => !regex.test((p || "") as string) && messages));
}

export function propOnlyDigits<T>(prop: KeysOfTypeGet<T, string | null | undefined>): PropValidator<T> {
    return propNotRegex(prop, /^[0-9]*$/, getText("OnlyDigits", "Field can contain only digits."));
}

export function propEmptyOr<T, U extends KeysOfTypeGet<T, any>>(prop: U, or: PropValidator<T>): PropValidator<T> {
    return entity => entity[prop] == null || entity[prop] === "" || entity[prop] === 0 ? { prop, messages: [] } : or(entity);
}

export function propRequired<T, U extends KeysOfTypeGet<T, any>>(prop: U, customMessage?: ValidationMessage, isValid?: (value: T[U]) => boolean): PropValidator<T> {
    return propValueValidator(prop, p => (isValid == null ? (p == null || p === "") : !isValid(p)) && (customMessage ?? getText("Required", "Required field.")));
}

export function propValidValue<T, U extends KeysOfTypeGet<T, string | number | null | undefined>>(prop: U, propFormatted: KeysOfTypeGet<T, string | undefined>): PropValidator<T> {
    return propValueValidator(prop, (p, e) => p == null && ((e[propFormatted] as string || "") !== "") && getInvalidValueText());
}

export function propInt<T, U extends KeysOfTypeGet<T, number | null | undefined>>(prop: U): PropValidator<T> {
    return propValueValidator(prop, p => p != null && (p as number) % 1 > 0 && getText("Integer", "Value must be an integer."));
}

export function propCurrency<T, U extends KeysOfTypeGet<T, number | null>>(prop: U, customMessage?: ValidationMessage): PropValidator<T> {
    const format = numberFormatByLandCode[useDefaultValues.getState().clientData.landCode];
    return propValueValidator(prop, p => p != null && getDecimalPlaces(p as number) > format.currencyDecimalPlaces && (customMessage || `Invalid currency value.`));
}

export function propMaxDecimalPlaces<T, U extends KeysOfTypeGet<T, number>>(prop: U, maxDecimalPlaces: number): PropValidator<T> {
    return propValueValidator(prop, p => p != null && getDecimalPlaces(p as number) > maxDecimalPlaces &&
        `Maximum decimal places is ${formatNumber(maxDecimalPlaces, useDefaultValues.getState().clientData.landCode, false)}`);
}

export function propNotZero<T, U extends KeysOfTypeGet<T, number | null | undefined>>(prop: U, customMessage?: ValidationMessage): PropValidator<T> {
    return propValueValidator(prop, p => p === 0 && (customMessage || `Value must not be zero.`));
}

export function propNumber<T, U extends KeysOfTypeGet<T, number | null | undefined>>(prop: U, checkNumber: (value: number, entity: T) => ValidationMessages): PropValidator<T> {
    return propValueValidator(prop, (p, e) => typeof p === "number" && checkNumber(p, e));
}

export function propMediaCode<T>(prop: KeysOfTypeGet<T, string | null | undefined>): PropValidator<T>[] {
    return [propOnlyDigits(prop), propLength(prop, 4, 4)];
}

export function propMinValue<T, U extends KeysOfTypeGet<T, number | null | undefined>>(prop: U, min: number): PropValidator<T> {
    return propNumber(prop, p => p < min && getText("MinValue", "Value must be {0} or more.")
        .replace("{0}", formatNumber(min, useDefaultValues.getState().clientData.landCode, false)));
}

export function propMaxValue<T, U extends KeysOfTypeGet<T, number | null | undefined>>(prop: U, max: number): PropValidator<T> {

    return propNumber(prop, p => p > max && getText("MaxValue", "Value must be {0} or less.")
        .replace("{0}", formatNumber(max, useDefaultValues.getState().clientData.landCode, false)));
}

export function propMinValueExclusive<T, U extends KeysOfTypeGet<T, number | null | undefined>>(prop: U, min: number): PropValidator<T> {
    return propNumber(prop, p => p <= min && `Value must be more than ${formatNumber(min, useDefaultValues.getState().clientData.landCode, false)}.`);
}

export function propMaxValueExclusive<T, U extends KeysOfTypeGet<T, number | null | undefined>>(prop: U, max: number): PropValidator<T> {
    return propNumber(prop, p => p >= max && `Value must be less than ${formatNumber(max, useDefaultValues.getState().clientData.landCode, false)}.`);
}

export function propMinDateExclusive<T, U extends KeysOfTypeGet<T, string | null | undefined>>(prop: U, min: string): PropValidator<T> {
    return propValueValidator(prop, p => p != null && p <= min && `Value must be more than ${formatDate(min, useDefaultValues.getState().clientData.landCode)}.`);
}

export function propMaxDateExclusive<T, U extends KeysOfTypeGet<T, string | null | undefined>>(prop: U, max: string): PropValidator<T> {
    return propValueValidator(prop, p => p != null && p >= max && `Value must be less than ${formatDate(max, useDefaultValues.getState().clientData.landCode)}.`);
}

export function propIf<T>(shouldValidate: (entity: T) => boolean, getValidator: () => PropValidator<T>, elseValidator?: () => PropValidator<T>): PropValidator<T> {
    return (entity: T) => shouldValidate(entity) ? getValidator()(entity) : elseValidator == null ? null : elseValidator()(entity);
}

export function propValidEmail<T, U extends KeysOfTypeGet<T, string | null | undefined>>(prop: U): PropValidator<T> {
    return propValueValidator(prop, p => p != null && p !== "" && !validateEmail(p as string) && "Invalid e-mail address.");
}

export function combine<T>(validators: PropValidator<T>[]): PropValidator<T> {
    return p => {
        for (const validator of validators) {
            const result = validator(p);
            if (result != null && result.messages.length > 0) {
                return result;
            }
        }

        return null;
    }
}

